import React, { useEffect, useState, useCallback, memo, useContext } from 'react';
import {
  VictoryAxis,
  VictoryBar,
  VictoryChart,
  VictoryGroup,
  VictoryLine,
  VictoryPie,
  VictoryTheme,
  VictoryTooltip,
  VictoryVoronoiContainer,
} from "victory";
import { ArrowDownOnSquareIcon, ChevronLeftIcon, ChevronRightIcon, MapIcon } from '@heroicons/react/20/solid';
import { MapPinIcon } from '@heroicons/react/16/solid';
import { AuthContext } from '../AuthContext';
import { ArrowRightCircleIcon, UserCircleIcon, DevicePhoneMobileIcon, XMarkIcon } from '@heroicons/react/24/solid';
import QRCode from 'react-qr-code';
import HolidayGraph from './holidaygraph';
import TaskGraph from './taskgraph';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { useNavigate } from 'react-router-dom';
import {
  differenceInHours,
  differenceInSeconds,
  endOfDay,
  endOfMonth,
  endOfQuarter,
  endOfWeek,
  endOfYear,
  format,
  parseISO,
  startOfDay,
  startOfMonth,
  startOfQuarter,
  startOfWeek,
  startOfYear,
  subDays,
  subMonths,
  subQuarters,
  subWeeks,
} from 'date-fns';
import Online from './online';
import Visitors from './visitors';
import WorkCoach from './workcoach';
import AdminInteractions from './admininteractionsnonav';
import { getCompanyLogoSrc } from '../utils/mediaUrl';
import DataLayerDash from './data-layerdash';

function Stats() {
  const [staff, setStaff] = useState([]);
  const [staffProfiles, setStaffProfiles] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [todayBudget, setTodayBudget] = useState(0);
  const [company, setCompany] = useState([]);
  const [logo, setLogo] = useState(null);
  const [projects, setProjects] = useState([]);
  const [revenueProjects, setRevenueProjects] = useState([]);
  const [shiftsLength, setShiftsLength] = useState(0);
  const [dialogMessage, setDialogMessage] = useState('');
  const [sickToday, setSickToday] = useState(0);
  const [holidayToday, setHolidayToday] = useState(0);
  const [dailyExpenditure, setDailyExpenditure] = useState(0);
  const [mobileLoginOpen, setMobileLoginOpen] = useState(false);
  const [mobileLoginUrl, setMobileLoginUrl] = useState('');
  const [mobileLoginExpiry, setMobileLoginExpiry] = useState('');
  const [mobileLoginError, setMobileLoginError] = useState('');
  const navigate = useNavigate();
  const user = useContext(AuthContext);

  // Get current date/time and day of week using date-fns
  const now = new Date();
  const today = format(now, 'EEEE');
  const todaysDate = format(now, 'yyyy-MM-dd');

  const isZeroHoursContract = (contractType = '') => {
    const normalized = String(contractType || '').trim().toLowerCase();
    return normalized === '0 hours' || normalized === '0 hour' || normalized === '0' || normalized.includes('zero hour');
  };

  const getWeeklyAccruedHours = (staffMember = {}) => {
    if (isZeroHoursContract(staffMember?.ContractType)) {
      const averageHours = Number(staffMember?.AverageHours) || 0;
      // For zero-hours contracts, AverageHours is already a weekly total.
      return averageHours;
    }

    // ContractedHours is stored as a weekly total for standard contracts.
    return Number(staffMember?.ContractedHours) || 0;
  };

  // Filter staff who are clocked in
  const staffClockedIn = staff.filter((staff) => staff.ClockOut === null);

  // Calculate total wages for staff currently clocked in
  const totalLabourCost = staffClockedIn.reduce((acc, staff) => {
    if (!staff.ClockIn) return acc;
    
    // Parse the clock-in time
    const [hours, minutes, seconds] = staff.ClockIn.split(':').map(Number);
    
    // Create date objects for calculation
    const clockInDate = new Date(now);
    clockInDate.setHours(hours, minutes, seconds || 0, 0);
    
    // Ensure the clockInDate is today (handle overnight shifts)
    if (clockInDate > now) {
      clockInDate.setDate(clockInDate.getDate() - 1);
    }
    
    // Calculate hours worked using date-fns
    const diffInSeconds = differenceInSeconds(now, clockInDate);
    const hoursWorked = diffInSeconds / 3600;
    
    const labourCost = hoursWorked * staff.HourlyRate;
    return acc + labourCost;
  }, 0);

  // Number of staff clocked in
  const staffClockIn = staff.length;

  const safeRevenue = (value) => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  };

  const parseDateSafe = (dateValue) => {
    if (!dateValue) return null;
    if (dateValue instanceof Date && !Number.isNaN(dateValue.getTime())) return dateValue;
    const parsedIso = parseISO(String(dateValue));
    if (!Number.isNaN(parsedIso.getTime())) return parsedIso;
    const parsedFallback = new Date(dateValue);
    return Number.isNaN(parsedFallback.getTime()) ? null : parsedFallback;
  };

  const normalizeSubscription = (value = '') => {
    const normalized = String(value).trim().toLowerCase();
    if (normalized === 'weekly' || normalized === 'week') return 'weekly';
    if (normalized === 'monthly' || normalized === 'month') return 'monthly';
    if (normalized === 'annual' || normalized === 'yearly' || normalized === 'year') return 'annual';
    return '';
  };

  const getSubscriptionPeriodAmount = (amount, subscription, period) => {
    const a = Number(amount) || 0;
    const sub = normalizeSubscription(subscription);
    if (!a || !sub) return 0;

    switch (period) {
      case 'weekly':
        if (sub === 'weekly') return a;
        if (sub === 'monthly') return a / 4;
        if (sub === 'annual') return a / 52;
        return 0;
      case 'monthly':
        if (sub === 'weekly') return a * 4;
        if (sub === 'monthly') return a;
        if (sub === 'annual') return a / 12;
        return 0;
      case 'quarterly':
        if (sub === 'weekly') return a * 13;
        if (sub === 'monthly') return a * 3;
        if (sub === 'annual') return a / 4;
        return 0;
      case 'yearly':
        if (sub === 'weekly') return a * 52;
        if (sub === 'monthly') return a * 12;
        if (sub === 'annual') return a;
        return 0;
      default:
        return 0;
    }
  };

  const isContractActiveOn = (customer, date) => {
    const amount = Number(customer?.Amount) || 0;
    const sub = normalizeSubscription(customer?.Subscription);
    if (!amount || !sub) return false;
    const start = customer?.ContractStart ? parseDateSafe(customer.ContractStart) : null;
    const end = customer?.ContractEnd ? parseDateSafe(customer.ContractEnd) : null;
    if (start && date < start) return false;
    if (end && date > end) return false;
    return true;
  };

  const getContractLtv = (contract) => {
    const amount = Number(contract?.Amount) || 0;
    const sub = normalizeSubscription(contract?.Subscription);
    if (!amount || !sub) return 0;

    const start = contract?.ContractStart ? parseDateSafe(contract.ContractStart) : null;
    if (!start) return 0;
    const end = contract?.ContractEnd ? parseDateSafe(contract.ContractEnd) : now;
    if (!end || end <= start) return 0;

    const elapsedDays = (end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24);
    if (sub === 'weekly') return (amount / 7) * elapsedDays;
    if (sub === 'monthly') return (amount / 30) * elapsedDays;
    return (amount / 365) * elapsedDays;
  };

  const getSubscriptionRevenueForPeriodAtDate = (period, dateRef) => {
    return customers
      .filter((customer) => isContractActiveOn(customer, dateRef))
      .reduce((sum, customer) => sum + getSubscriptionPeriodAmount(customer?.Amount, customer?.Subscription, period), 0);
  };

  const getProjectDate = (project) => {
    const raw = project?.StartDate || project?.created_at || project?.CreatedAt || null;
    if (!raw) return null;
    const parsed = new Date(raw);
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const sumRevenueInRange = (items, start, end) => {
    return items.reduce((sum, project) => {
      const projectDate = getProjectDate(project);
      if (!projectDate) return sum;
      if (projectDate >= start && projectDate <= end) {
        return sum + safeRevenue(project?.Price);
      }
      return sum;
    }, 0);
  };

  const dailyRevenue = sumRevenueInRange(revenueProjects, startOfDay(now), endOfDay(now));
  const yesterday = subDays(now, 1);
  const yesterdayRevenue = sumRevenueInRange(revenueProjects, startOfDay(yesterday), endOfDay(yesterday));

  const weeklyRevenue = sumRevenueInRange(
    revenueProjects,
    startOfWeek(now, { weekStartsOn: 1 }),
    endOfWeek(now, { weekStartsOn: 1 })
  );
  const lastWeekRef = subWeeks(now, 1);
  const lastWeekRevenue = sumRevenueInRange(
    revenueProjects,
    startOfWeek(lastWeekRef, { weekStartsOn: 1 }),
    endOfWeek(lastWeekRef, { weekStartsOn: 1 })
  );

  const monthlyRevenue = sumRevenueInRange(revenueProjects, startOfMonth(now), endOfMonth(now));
  const lastMonthRef = subMonths(now, 1);
  const lastMonthRevenue = sumRevenueInRange(revenueProjects, startOfMonth(lastMonthRef), endOfMonth(lastMonthRef));

  const quarterlyRevenue = sumRevenueInRange(revenueProjects, startOfQuarter(now), endOfQuarter(now));
  const lastQuarterRef = subQuarters(now, 1);
  const lastQuarterRevenue = sumRevenueInRange(
    revenueProjects,
    startOfQuarter(lastQuarterRef),
    endOfQuarter(lastQuarterRef)
  );

  const yearlyRevenue = sumRevenueInRange(revenueProjects, startOfYear(now), endOfYear(now));

  const activeContracts = customers.filter((customer) => isContractActiveOn(customer, now));
  const subscriptionContracts = customers.filter((customer) => {
    const amount = Number(customer?.Amount) || 0;
    const sub = normalizeSubscription(customer?.Subscription);
    const start = customer?.ContractStart ? parseDateSafe(customer.ContractStart) : null;
    return !!amount && !!sub && !!start;
  });

  const dailySubscriptionRevenue = 0;
  const yesterdaySubscriptionRevenue = 0;
  const weeklySubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('weekly', now);
  const lastWeekSubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('weekly', lastWeekRef);
  const monthlySubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('monthly', now);
  const lastMonthSubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('monthly', lastMonthRef);
  const quarterlySubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('quarterly', now);
  const lastQuarterSubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('quarterly', lastQuarterRef);
  const yearlySubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('yearly', now);

  const dailyRevenueTotal = dailyRevenue;
  const yesterdayRevenueTotal = yesterdayRevenue;
  const weeklyRevenueTotal = weeklyRevenue + weeklySubscriptionRevenue;
  const lastWeekRevenueTotal = lastWeekRevenue + lastWeekSubscriptionRevenue;
  const monthlyRevenueTotal = monthlyRevenue + monthlySubscriptionRevenue;
  const lastMonthRevenueTotal = lastMonthRevenue + lastMonthSubscriptionRevenue;
  const quarterlyRevenueTotal = quarterlyRevenue + quarterlySubscriptionRevenue;
  const lastQuarterRevenueTotal = lastQuarterRevenue + lastQuarterSubscriptionRevenue;
  const yearlyRevenueTotal = yearlyRevenue + yearlySubscriptionRevenue;

  const getAccruedStaffCostForPeriod = (period, dateRef) => {
    const multiplierByPeriod = {
      weekly: 1,
      monthly: 52 / 12,
      quarterly: 13,
      yearly: 52,
    };

    const multiplier = multiplierByPeriod[period] || 0;
    if (!multiplier) return 0;

    return staffProfiles.reduce((sum, staffMember) => {
      const start = staffMember?.StartDate ? parseDateSafe(staffMember.StartDate) : null;
      const end = staffMember?.EndDate ? parseDateSafe(staffMember.EndDate) : null;

      if (start && dateRef < start) return sum;
      if (end && dateRef > end) return sum;

      const weeklyHours = getWeeklyAccruedHours(staffMember);
      const hourlyRate = Number(staffMember?.HourlyRate) || 0;

      return sum + (weeklyHours * hourlyRate * multiplier);
    }, 0);
  };

  const weeklyStaffCost = getAccruedStaffCostForPeriod('weekly', now);
  const monthlyStaffCost = getAccruedStaffCostForPeriod('monthly', now);
  const quarterlyStaffCost = getAccruedStaffCostForPeriod('quarterly', now);
  const yearlyStaffCost = getAccruedStaffCostForPeriod('yearly', now);

  const weeklyProfit = weeklyRevenueTotal - weeklyStaffCost;
  const monthlyProfit = monthlyRevenueTotal - monthlyStaffCost;
  const quarterlyProfit = quarterlyRevenueTotal - quarterlyStaffCost;
  const yearlyProfit = yearlyRevenueTotal - yearlyStaffCost;

  const integratedPeriodStats = [
    {
      key: 'weekly',
      name: 'Weekly Profit',
      revenue: weeklyRevenueTotal,
      staffCost: weeklyStaffCost,
      profit: weeklyProfit,
    },
    {
      key: 'monthly',
      name: 'Monthly Profit',
      revenue: monthlyRevenueTotal,
      staffCost: monthlyStaffCost,
      profit: monthlyProfit,
    },
    {
      key: 'quarterly',
      name: 'Quarterly Profit',
      revenue: quarterlyRevenueTotal,
      staffCost: quarterlyStaffCost,
      profit: quarterlyProfit,
    },
    {
      key: 'yearly',
      name: 'Yearly Profit',
      revenue: yearlyRevenueTotal,
      staffCost: yearlyStaffCost,
      profit: yearlyProfit,
    },
  ];

  const monthlyProfitTrend = Array.from({ length: 6 }, (_, idx) => {
    const monthsAgo = 5 - idx;
    const monthRef = subMonths(now, monthsAgo);
    const monthStart = startOfMonth(monthRef);
    const monthEnd = endOfMonth(monthRef);

    const monthProjectRevenue = sumRevenueInRange(revenueProjects, monthStart, monthEnd);
    const monthSubscriptionRevenue = getSubscriptionRevenueForPeriodAtDate('monthly', monthRef);
    const monthRevenueTotal = monthProjectRevenue + monthSubscriptionRevenue;
    const monthStaffCost = getAccruedStaffCostForPeriod('monthly', monthRef);
    const monthProfit = monthRevenueTotal - monthStaffCost;
    const monthLabel = format(monthRef, 'MMM yy');

    return {
      x: monthLabel,
      revenue: monthRevenueTotal,
      staffCost: monthStaffCost,
      profit: monthProfit,
      label: `${monthLabel}\nRevenue: £${monthRevenueTotal.toFixed(2)}\nStaff: £${monthStaffCost.toFixed(2)}\nProfit: £${monthProfit.toFixed(2)}`,
    };
  });

  const subscriptionLtvTotal = subscriptionContracts.reduce((sum, contract) => sum + getContractLtv(contract), 0);
  const monthlyRecurringSubscriptions = activeContracts.reduce(
    (sum, contract) => sum + getSubscriptionPeriodAmount(contract?.Amount, contract?.Subscription, 'monthly'),
    0
  );

  const formatRevenueChange = (current, previous, label) => {
    const delta = current - previous;
    const sign = delta >= 0 ? '+' : '-';
    const absDelta = Math.abs(delta);
    const percentage = previous > 0 ? `${sign}${((absDelta / previous) * 100).toFixed(1)}%` : 'n/a';
    return `${sign}£${absDelta.toFixed(2)} vs ${label} (${percentage})`;
  };

  const revenueStats = [
    {
      name: 'Daily Revenue',
      stat: `£${dailyRevenueTotal.toFixed(2)}`,
      comparison: formatRevenueChange(dailyRevenueTotal, yesterdayRevenueTotal, 'yesterday'),
      projectRevenue: dailyRevenue,
      subscriptionRevenue: dailySubscriptionRevenue,
      showSubscriptionBreakdown: false,
    },
    {
      name: 'Weekly Revenue',
      stat: `£${weeklyRevenueTotal.toFixed(2)}`,
      comparison: formatRevenueChange(weeklyRevenueTotal, lastWeekRevenueTotal, 'last week'),
      projectRevenue: weeklyRevenue,
      subscriptionRevenue: weeklySubscriptionRevenue,
      showSubscriptionBreakdown: true,
    },
    {
      name: 'Monthly Revenue',
      stat: `£${monthlyRevenueTotal.toFixed(2)}`,
      comparison: formatRevenueChange(monthlyRevenueTotal, lastMonthRevenueTotal, 'last month'),
      projectRevenue: monthlyRevenue,
      subscriptionRevenue: monthlySubscriptionRevenue,
      showSubscriptionBreakdown: true,
    },
    {
      name: 'Quarterly Revenue',
      stat: `£${quarterlyRevenueTotal.toFixed(2)}`,
      comparison: formatRevenueChange(quarterlyRevenueTotal, lastQuarterRevenueTotal, 'last quarter'),
      projectRevenue: quarterlyRevenue,
      subscriptionRevenue: quarterlySubscriptionRevenue,
      showSubscriptionBreakdown: true,
    },
    {
      name: 'Yearly Revenue',
      stat: `£${yearlyRevenueTotal.toFixed(2)}`,
      comparison: 'Year-to-date total',
      projectRevenue: yearlyRevenue,
      subscriptionRevenue: yearlySubscriptionRevenue,
      showSubscriptionBreakdown: true,
    },
  ];

  const milestoneActualRevenue = {
    Weekly: weeklyRevenueTotal,
    Monthly: monthlyRevenueTotal,
    Quarterly: quarterlyRevenueTotal,
    Yearly: yearlyRevenueTotal,
  };

  // Simplified stats array
  const stats = [
    { name: 'Staff Clocked In', stat: staffClockIn },
    { name: 'Shifts Today', stat: shiftsLength },
    { name: 'Sick Today', stat: sickToday },
    { name: 'Holiday Today', stat: holidayToday },
    { name: 'Live Wages Today (£)', stat: "£" + totalLabourCost.toFixed(2) + " / £" + dailyExpenditure.toFixed(2) },
    { name: "Today's Budget (£)", stat: "£" + todayBudget.toFixed(2) },
  ];

  // Percentage of today's budget used
  const percentageUsed = todayBudget > 0 ? (totalLabourCost / todayBudget) * 100 : 0;

  // Data for pie chart
  const pieData = [
    { name: 'Budget', value: totalLabourCost },
    { name: 'Remaining', value: Math.max(todayBudget - totalLabourCost, 0) },
  ];

  const COLORS = ["rgb(241, 0, 52)", "rgb(16, 185, 129)"];

  // Fetch clock-ins on mount and poll every minute
  useEffect(() => {
    const fetchClockIns = async () => {
      try {
        const clockIns = await fetch('https://api.insightos.co.uk/api/clockins', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });


        const shiftsToday = await fetch(`https://api.insightos.co.uk/api/shiftsday/${todaysDate}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        const response5 = await fetch(`https://api.insightos.co.uk/api/dailyexpenditure/${todaysDate}`, {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response5.ok) throw new Error('Failed to fetch daily expenditure');

        const data5 = await response5.json();

        console.log(data5);

        const data2 = await shiftsToday.json();

        console.log(data5);
        setDailyExpenditure(data5.daily || 0);

setShiftsLength(data2.data.length);
setSickToday(data2.data.filter(shift => shift.Sick === 1).length);
setHolidayToday(data2.data.filter(shift => shift.Holiday === 1).length);







        if (!clockIns.ok) throw new Error('Failed to fetch clock ins');
        const data = await clockIns.json();
        setStaff(data.data);

        const staffProfilesResponse = await fetch('https://api.insightos.co.uk/api/staff', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!staffProfilesResponse.ok) throw new Error('Failed to fetch staff profiles');
        const staffProfilesData = await staffProfilesResponse.json();
        setStaffProfiles(Array.isArray(staffProfilesData?.data) ? staffProfilesData.data : []);

        const pastDates = data.data.filter((clockIn) => {
          const clockInDate = new Date(clockIn.Date);
          return clockInDate.toDateString() !== now.toDateString();
        });

        if (pastDates.length > 0) {
          const pastStaffNames = pastDates.map(clockIn => clockIn.StaffName).join(', ');
          setDialogMessage(`Clock-ins from previous days: ${pastStaffNames}, please recalibrate your clock-ins.`);
        }
      } catch (error) {
        console.error('Failed to fetch clock ins:', error);
      }
    };

    fetchClockIns();
    const intervalId = setInterval(fetchClockIns, 60000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        const response = await fetch(`https://api.insightos.co.uk/api/getprojecttoday`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          setProjects(data.data);
        }
      } catch (error) {
        console.error('Error fetching projects:', error);
      }
    };

    fetchProjects();
  }, []);

  // Fetch budgets and find today's budget
  useEffect(() => {
    const fetchBudget = async () => {
      try {
        const budget = await fetch('https://api.insightos.co.uk/api/operations', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!budget.ok) throw new Error('Failed to fetch budget');
        const data = await budget.json();

        const budgets = data.data;
        const MonthlyBudget = budgets[0].Budget;
        const daysOpen = budgets[0].DaysOpen; // per week 
        const dailyBudget = MonthlyBudget / (daysOpen * 4); // Assuming 4 weeks in a month

        setTodayBudget(dailyBudget);
      } catch (error) {
        console.error('Failed to fetch budget:', error);
      }
    };

    fetchBudget();
    const intervalId = setInterval(fetchBudget, 60000);
    return () => clearInterval(intervalId);
  }, [today]);

  useEffect(() => {
    const fetchCompany = async () => {
      try {
        const cachedLogoData = localStorage.getItem('companyLogoData');
        const cachedCompanyData = localStorage.getItem('companyData');
        const cachedTimestamp = localStorage.getItem('companyLogoTimestamp');
        
        if (cachedLogoData && cachedCompanyData && cachedTimestamp) {
          const timestamp = parseInt(cachedTimestamp);
          const now = new Date().getTime();
          const oneDay = 24 * 60 * 60 * 1000;
          
          if (now - timestamp < oneDay) {
            setLogo(cachedLogoData);
            setCompany(JSON.parse(cachedCompanyData));
            return;
          }
        }
        
        const response = await fetch(`https://api.insightos.co.uk/api/company`, {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
          },
          credentials: 'include',
        });

        if (response.ok) {
          const data = await response.json();
          
          if (data.data && data.data.length > 0) {
            const logoSrc = getCompanyLogoSrc(data.data[0]);

            if (logoSrc) {
              localStorage.setItem('companyLogoData', logoSrc);
              localStorage.setItem('companyData', JSON.stringify(data.data));
              localStorage.setItem('companyLogoTimestamp', new Date().getTime().toString());

              setLogo(logoSrc);
            } else if (cachedLogoData) {
              setLogo(cachedLogoData);
            }
          } else {
            console.warn('Company logo data is missing or invalid');
            if (cachedLogoData) {
              setLogo(cachedLogoData);
            }
          }
          
          setCompany(data.data);
        }
      } catch (error) {
        console.error('Error fetching company data:', error);
        
        const cachedLogoData = localStorage.getItem('companyLogoData');
        const cachedCompanyData = localStorage.getItem('companyData');
        
        if (cachedLogoData && cachedCompanyData) {
          setLogo(cachedLogoData);
          setCompany(JSON.parse(cachedCompanyData));
        }
      }
    };

    fetchCompany();
  }, []);

  useEffect(() => {
    const fetchRevenueProjects = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/getproject', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch project revenue data');
        const data = await response.json();
        setRevenueProjects(Array.isArray(data?.data) ? data.data : []);
      } catch (error) {
        console.error('Failed to fetch project revenue data:', error);
        setRevenueProjects([]);
      }
    };

    fetchRevenueProjects();
  }, []);

  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await fetch('https://api.insightos.co.uk/api/customers', {
          method: 'GET',
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
        });

        if (!response.ok) throw new Error('Failed to fetch customers');
        const data = await response.json();
        setCustomers(Array.isArray(data?.customers) ? data.customers : []);
      } catch (error) {
        console.error('Failed to fetch customers:', error);
        setCustomers([]);
      }
    };

    fetchCustomers();
  }, []);

  const handleClockOutAll = async () => {
    try {
      const clockInResponse = await fetch('https://api.insightos.co.uk/api/clockoutall', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include',
      });
      
      const clockInData = await clockInResponse.json();
      if (!clockInResponse.ok) {
        throw new Error(clockInData.message || 'Failed to clock out all staff.');
      }
      console.log('All staff clocked out successfully.');
      window.location.reload();
    } catch (error) {
      console.error('Error clocking out all staff:', error);
    }
  };

  const handleMobileLogin = async () => {
    setMobileLoginError('');
    try {
      const response = await fetch('https://api.insightos.co.uk/api/mobilegeneratecode', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.message || 'Failed to generate mobile login code');
      }

      const data = await response.json();
      if (!data?.success || !data?.tempcode || !data?.id) {
        throw new Error('Invalid response from server');
      }

      const url = `https://app.insightos.co.uk/mobilelogin/${data.tempcode}/${data.id}`;
      setMobileLoginUrl(url);
      setMobileLoginExpiry(data.expiresAt || '');
      setMobileLoginOpen(true);
    } catch (error) {
      setMobileLoginError(error?.message || 'Failed to generate mobile login code');
    }
  };

  return (
    <>
  <div className="w-full px-0 sm:px-0" style={{ position: 'relative', zIndex: 0 }}>
        <div>
          <div>
            {company.map((company) => (
              <div
                key={company.CompanyName}
                className="relative overflow-hidden flex flex-col sm:flex-row items-start sm:items-center justify-between p-3 sm:p-4 rounded-2xl border border-white/35 bg-gradient-to-r from-indigo-900 via-sky-500 to-emerald-500 backdrop-blur-xl shadow-[0_14px_35px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.35)]"
                style={{ position: 'relative', zIndex: 'auto' }}
              >  
                <div className="pointer-events-none absolute -top-8 -left-8 h-24 w-24 rounded-full bg-white/25 blur-2xl" />
                <div className="pointer-events-none absolute -bottom-10 -right-8 h-24 w-24 rounded-full bg-sky-300/20 blur-2xl" />
                <div className="min-w-0 flex-1 flex flex-col sm:flex-row items-start sm:items-center sm:space-x-4 space-y-2 sm:space-y-0">
                  <h2 className="lg:text-md font-bold text-white sm:text-2xl/7 sm:tracking-tight">
                    {company.CompanyName}
                  </h2>
                  
                  {logo ? (
                    <img
                      src={logo}
                      alt="Company Logo"
                      className="h-8 w-8 sm:h-10 sm:w-10 object-cover rounded-full bg-opacity-50 shadow"
                      loading="lazy"
                      decoding="async"
                      style={{ position: 'relative', zIndex: 'auto' }}
                      onError={(e) => {
                        console.error('Error loading company logo');
                        e.target.style.display = 'none';
                      }}
                    />
                  ) : (
                    <div className="h-10 w-10 flex items-center justify-center rounded-full bg-gray-300 bg-opacity-50 shadow">
                      <span className="text-gray-600">{company.CompanyName?.charAt(0) || '?'}</span>
                    </div>
                  )}
                </div>

                <div className="mt-2 sm:mt-0 flex flex-col sm:flex-row sm:flex-wrap sm:space-x-6">
                  <div className="flex items-center space-x-2 mb-1 sm:mb-0">
                    <UserCircleIcon
                      className="flex-shrink-0 h-4 w-4 sm:h-5 sm:w-5 text-green-200"
                      aria-hidden="true"
                    />
                    <div className="flex items-center gap-2">
                      <p className="text-xs sm:text-sm text-white">{user.name}</p>
                      <button
                        type="button"
                        onClick={handleMobileLogin}
                        className="inline-flex items-center gap-1 rounded-full bg-white/20 px-2 py-1 text-[10px] font-semibold text-white hover:bg-white/30"
                        title="Mobile login"
                      >
                        <DevicePhoneMobileIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                  <div className="flex items-center space-x-2">
                    <MapPinIcon
                      className="flex-shrink-0 h-4 w-4 sm:h-5 sm:w-5 text-green-200"
                      aria-hidden="true"
                    />
                    <p className="text-xs sm:text-sm text-white">{company.City}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>

        <dl className="mt-4 grid grid-cols-1 gap-4 sm:gap-0 sm:divide-y divide-white/25 overflow-hidden rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/65 via-sky-100/60 to-emerald-100/55 backdrop-blur-xl shadow-[0_14px_35px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.35)] sm:grid-cols-3 sm:divide-x sm:divide-y-0">
          {stats.map((item) => (
            <div key={item.name} className="px-3 py-4 sm:px-4 sm:py-5">
              <dt className="text-sm sm:text-base font-medium text-indigo-900/80">{item.name}</dt>
              <dd className="mt-1 text-lg sm:text-2xl font-extrabold text-indigo-900/90">
                {item.stat}
              </dd>
            </div>
          ))}
        </dl>

        <DataLayerDash />

        {dialogMessage && (
          <div className="relative overflow-hidden flex flex-col mt-4 p-3 sm:p-4 rounded-2xl border border-red-200/45 bg-gradient-to-br from-red-500/85 via-rose-500/80 to-orange-500/80 text-white backdrop-blur-xl shadow-[0_14px_35px_rgba(127,29,29,0.35),inset_0_1px_0_rgba(255,255,255,0.25)]">
            <div className="pointer-events-none absolute -top-8 -right-8 h-24 w-24 rounded-full bg-white/15 blur-2xl" />
            <span className="text-sm sm:text-base">{dialogMessage}</span>
            <button
              onClick={handleClockOutAll}
              className="mt-2 w-full sm:w-auto inline-flex justify-center items-center gap-x-2 rounded-md bg-red-600 px-3 py-2 sm:px-3.5 sm:py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-red-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-red-600"
            >
              <ArrowDownOnSquareIcon aria-hidden="true" className="-ml-0.5 size-4 sm:size-5" />
              Recalibrate
            </button>
          </div>
        )}

        {mobileLoginOpen && (
          <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
            <div className="w-full max-w-sm rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/80 via-white/85 to-sky-100/75 p-4 backdrop-blur-xl shadow-[0_16px_40px_rgba(15,23,42,0.35),inset_0_1px_0_rgba(255,255,255,0.45)]">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-lg font-semibold text-gray-900">Mobile Login</h3>
                <button
                  type="button"
                  onClick={() => setMobileLoginOpen(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              </div>

              {mobileLoginError && (
                <div className="mb-3 text-sm text-red-600">{mobileLoginError}</div>
              )}

              {mobileLoginUrl && (
                <div className="flex flex-col items-center gap-3">
                  <div className="bg-white p-3 rounded-lg border border-gray-200">
                    <QRCode value={mobileLoginUrl} size={180} />
                  </div>
                  <p className="text-xs text-gray-500 text-center break-all">{mobileLoginUrl}</p>
                  {mobileLoginExpiry && (
                    <p className="text-xs text-gray-500">Expires: {mobileLoginExpiry}</p>
                  )}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div>
        <ul role="list" className="mt-6 sm:mt-8 grid grid-cols-1 gap-4 sm:gap-5">
          {projects.map((project) => (
            <li key={project.id} className="col-span-1 flex rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/70 via-sky-100/65 to-emerald-100/60 backdrop-blur-xl shadow-[0_12px_30px_rgba(15,23,42,0.22),inset_0_1px_0_rgba(255,255,255,0.35)] overflow-hidden">
              <div className="flex w-12 sm:w-16 shrink-0 items-center bg-gradient-to-b from-indigo-700/90 via-indigo-600/85 to-sky-600/85 justify-center text-sm font-medium text-white">
              </div>
              <div className="flex flex-1 items-center justify-between truncate bg-transparent">
                <div className="flex-1 truncate px-3 sm:px-4 py-2 text-xs sm:text-sm">
                  <button onClick={() => navigate('/project-management', { state: { projectId: project.id } })} className="font-medium text-gray-900 hover:text-gray-600">
                    Current Project: {project.Name}
                  </button>
                  <p className="text-gray-500">Date: {format(new Date(project.StartDate), 'dd MMM yyyy')} to {format(new Date(project.EndDate), 'dd MMM yyyy')}</p>
                  <p className="text-gray-500">Staff: {project.Staff}</p>
                  {project.ProjectType === "External" ? (
                    <div className="flex items-center space-x-1">
                      <p className="text-gray-500"> Client: {project.ClientName}</p>
                      <p className="text-gray-500"> Price: £{project.Price} </p>
                      <p className="text-gray-500"> Distance: {project.Distance}</p>
                    </div>
                  ) : null}
                </div>
                <div className="px-3 sm:px-4 py-2">
                  <p className="text-emerald-500 animate-pulse">Active</p>
                </div>
                <div className="shrink-0 pr-1 sm:pr-2">
                  <button
                    onClick={() => navigate('/project-management', { state: { projectId: project.id } })}
                    type="button"
                    className="inline-flex size-8 items-center justify-center rounded-full bg-transparent bg-white text-emerald-500 hover:text-emerald-600 hover:animate-bounce focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    <span className="sr-only">Open options</span>
                    <ArrowRightCircleIcon aria-hidden="true" className="size-4 sm:size-5" />
                  </button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <div className="mt-6 sm:mt-8">
        <div className="px-2 sm:px-0">
          <Online />
        </div>
        <div className="mt-4 px-2 sm:px-0">
          <Visitors />
        </div>
        <div className="mt-4 px-2 sm:px-0">
          <WorkCoach />
        </div>
      </div>
      
      <div className='grid grid-cols-1 pt-4 sm:pt-5 gap-4 px-2 sm:px-0'>
        <div className="mt-2 sm:mt-4 rounded-2xl border border-white/35 bg-gradient-to-br from-indigo-100/75 via-sky-100/70 to-purple-100/65 backdrop-blur-xl p-3 sm:p-4 shadow-[0_14px_35px_rgba(15,23,42,0.25),inset_0_1px_0_rgba(255,255,255,0.35)]">
          <h1 className="text-xl sm:text-3xl font-bold mb-2 sm:mb-4 text-center text-indigo-900/90">Budget Usage</h1>
          <div className="flex justify-center mt-2 sm:mt-4 rounded-2xl border border-white/35 bg-gradient-to-br from-white/85 via-sky-50/80 to-indigo-50/80 p-3 sm:p-6 shadow-[0_10px_24px_rgba(15,23,42,0.2),inset_0_1px_0_rgba(255,255,255,0.45)]">
            <svg width="100%" height="100%" viewBox="0 0 300 300" style={{ maxWidth: '280px' }}>
              <defs>
                <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
                  <feDropShadow dx="0" dy="4" stdDeviation="6" floodColor="rgba(0, 0, 0, 0.1)" />
                </filter>
              </defs>
              <VictoryPie
                standalone={false}
                data={pieData}
                x="name"
                y="value"
                width={300}
                height={300}
                padAngle={3}
                innerRadius={40}
                labels={({ datum }) => `${datum.name}\n${((datum.value / todayBudget) * 100).toFixed(0)}%`}
                style={{
                  labels: { fill: "#1e40af", fontSize: 7, fontWeight: "bold" },
                  data: {
                    stroke: "#FFF",
                    strokeWidth: 2,
                    filter: "url(#shadow)",
                  },
                }}
                colorScale={["#7c3aed", "#1e40af"]}
              />
            </svg>
          </div>
          <p className="mt-2 text-xs sm:text-sm text-center text-indigo-900/80">
            {percentageUsed.toFixed(2)}% of today's budget has been used.
          </p>
        </div>
        <div className='grid grid-cols-1 gap-4 mt-2'>
          <div className="px-0 sm:px-0">
            <HolidayGraph />
          </div>
        </div>
        <div className='grid grid-cols-1 gap-4 mt-2'>
          <div className="px-0 sm:px-0">
            <TaskGraph />
          </div>
          {user.access === "admin" && (
            <div className="mt-5 mb-5">
              <AdminInteractions />
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default memo(Stats);
